import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Select from 'react-select';
import { SUBJECTS } from '../../../utils/studyPlanMockData';
import { SubjectStrength } from '../../../types/study-plan';

interface SubjectStrengthStepProps {
  onNext: (data: { subjectStrengths: SubjectStrength[] }) => void;
  onBack: () => void;
  examType: string;
  defaultValue?: { subjectStrengths: SubjectStrength[] };
}

const SubjectStrengthStep: React.FC<SubjectStrengthStepProps> = ({ 
  onNext, 
  onBack, 
  examType, 
  defaultValue 
}) => {
  const [subjectStrengths, setSubjectStrengths] = useState<SubjectStrength[]>(
    defaultValue?.subjectStrengths || []
  );

  const getRelevantSubjects = () => {
    const examTypeLower = examType.toLowerCase();
    if (examTypeLower === 'neet') {
      return SUBJECTS.filter(s => ['physics', 'chemistry', 'biology'].includes(s.id));
    }
    return SUBJECTS.filter(s => ['physics', 'chemistry', 'mathematics'].includes(s.id));
  };

  const handleStrengthChange = (subjectId: string, level: 'very-strong' | 'moderately-strong' | 'weak') => {
    setSubjectStrengths(prev => {
      const existing = prev.find(s => s.subjectId === subjectId);
      if (existing) {
        return prev.map(s => 
          s.subjectId === subjectId 
            ? { ...s, level, selectedChapters: level === 'weak' ? s.selectedChapters : [] }
            : s
        );
      } else {
        return [...prev, { subjectId, level, selectedChapters: [] }];
      }
    });
  };

  const handleChapterSelection = (subjectId: string, selectedChapters: string[]) => {
    setSubjectStrengths(prev => 
      prev.map(s => 
        s.subjectId === subjectId ? { ...s, selectedChapters } : s
      )
    );
  };

  const getSubjectStrength = (subjectId: string) => {
    return subjectStrengths.find(s => s.subjectId === subjectId);
  };

  const getChapterOptions = (subjectId: string) => {
    const subject = SUBJECTS.find(s => s.id === subjectId);
    return subject?.chapters.map(chapter => ({
      value: chapter.id,
      label: chapter.name
    })) || [];
  };

  const getSelectedChapters = (subjectId: string) => {
    const strength = getSubjectStrength(subjectId);
    const options = getChapterOptions(subjectId);
    return options.filter(option => strength?.selectedChapters.includes(option.value));
  };

  const getStrengthColor = (level: string) => {
    switch (level) {
      case 'very-strong': return 'success';
      case 'moderately-strong': return 'warning';
      case 'weak': return 'danger';
      default: return 'secondary';
    }
  };

  const getStrengthIcon = (level: string) => {
    switch (level) {
      case 'very-strong': return 'fa-star';
      case 'moderately-strong': return 'fa-star-half-alt';
      case 'weak': return 'fa-exclamation-triangle';
      default: return 'fa-question';
    }
  };

  const handleSubmit = () => {
    const relevantSubjects = getRelevantSubjects();
    const allSubjectsAssessed = relevantSubjects.every(subject => 
      subjectStrengths.some(s => s.subjectId === subject.id)
    );

    if (!allSubjectsAssessed) {
      alert('Please assess your strength in all subjects');
      return;
    }

    onNext({ subjectStrengths });
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.3 }}
      className="step-content"
    >
      <div className="container-fluid py-4">
        <div className="row justify-content-center">
          <div className="col-lg-10">
            <div className="text-center mb-5">
              <h2 className="display-6 fw-bold text-primary mb-3">
                Assess Your Subject Strengths
              </h2>
              <p className="lead text-muted">
                Help us understand your current proficiency in each subject
              </p>
            </div>

            <div className="row g-4 mb-5">
              {getRelevantSubjects().map((subject) => {
                const strength = getSubjectStrength(subject.id);
                return (
                  <div key={subject.id} className="col-12">
                    <div className="card border-light shadow-sm">
                      <div className="card-header bg-light">
                        <h5 className="mb-0 text-dark fw-bold">
                          <i className="fas fa-book me-2 text-primary"></i>
                          {subject.name}
                        </h5>
                      </div>
                      <div className="card-body">
                        <div className="row">
                          {/* Strength Selection Column */}
                          <div className="col-md-6">
                            <h6 className="text-dark mb-3">
                              <i className="fas fa-chart-line me-2 text-primary"></i>
                              Select Your Strength Level
                            </h6>
                            <div className="d-grid gap-2">
                              {['very-strong', 'moderately-strong', 'weak'].map((level) => (
                                <motion.div
                                  key={level}
                                  whileHover={{ scale: 1.02 }}
                                  whileTap={{ scale: 0.98 }}
                                  className={`card border-2 cursor-pointer position-relative ${
                                    strength?.level === level
                                      ? 'border-primary bg-primary shadow-sm'
                                      : 'border-light hover-shadow'
                                  }`}
                                  onClick={() => handleStrengthChange(subject.id, level as any)}
                                >
                                  {strength?.level === level && (
                                    <div className="position-absolute top-0 end-0 m-1">
                                      <div className="bg-white text-primary rounded-circle d-flex align-items-center justify-content-center border border-primary" 
                                           style={{ width: '20px', height: '20px' }}>
                                        <i className="fas fa-check" style={{ fontSize: '10px' }}></i>
                                      </div>
                                    </div>
                                  )}
                                  <div className="card-body p-2">
                                    <div className={`d-flex align-items-center ${
                                      strength?.level === level ? 'text-white' : `text-${getStrengthColor(level)}`
                                    }`}>
                                      <i className={`fas ${getStrengthIcon(level)} me-3`}></i>
                                      <div>
                                        <h6 className={`fw-bold mb-0 ${
                                          strength?.level === level ? 'text-white' : 'text-dark'
                                        }`}>
                                          {level === 'very-strong' && 'Very Strong'}
                                          {level === 'moderately-strong' && 'Moderately Strong'}
                                          {level === 'weak' && 'Weak'}
                                        </h6>
                                        <small className={`${
                                          strength?.level === level ? 'text-white opacity-75' : 'text-muted'
                                        }`}>
                                          {level === 'very-strong' && 'Confident in most topics'}
                                          {level === 'moderately-strong' && 'Good in some areas'}
                                          {level === 'weak' && 'Need focused study'}
                                        </small>
                                      </div>
                                    </div>
                                  </div>
                                </motion.div>
                              ))}
                            </div>
                          </div>

                          {/* Chapter Selection Column */}
                          <div className="col-md-6">
                            <h6 className="text-dark mb-3">
                              <i className="fas fa-list me-2 text-primary"></i>
                              Chapter Selection
                            </h6>
                            {!strength?.level && (
                              <div className="text-center py-5">
                                <i className="fas fa-arrow-left text-muted fa-2x mb-2"></i>
                                <p className="text-muted">Please select your strength level first</p>
                              </div>
                            )}
                            
                            {strength?.level === 'weak' && (
                              <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                className="bg-light rounded p-3"
                              >
                                <label className="form-label fw-semibold text-dark mb-2">
                                  Chapters needing help:
                                </label>
                                <Select
                                  isMulti
                                  options={getChapterOptions(subject.id)}
                                  value={getSelectedChapters(subject.id)}
                                  onChange={(selected) => 
                                    handleChapterSelection(
                                      subject.id, 
                                      selected ? selected.map(option => option.value) : []
                                    )
                                  }
                                  placeholder="Choose chapters to focus on..."
                                  className="react-select-container"
                                  classNamePrefix="react-select"
                                  styles={{
                                    control: (base) => ({
                                      ...base,
                                      borderColor: '#e9ecef',
                                      '&:hover': { borderColor: '#0d6efd' },
                                      boxShadow: 'none'
                                    })
                                  }}
                                />
                                <small className="text-muted mt-2 d-block">
                                  Select chapters where you need the most improvement
                                </small>
                              </motion.div>
                            )}

                            {strength?.level === 'moderately-strong' && (
                              <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                className="bg-light rounded p-3"
                              >
                                <label className="form-label fw-semibold text-dark mb-2">
                                  Additional practice:
                                </label>
                                <Select
                                  isMulti
                                  options={getChapterOptions(subject.id)}
                                  value={getSelectedChapters(subject.id)}
                                  onChange={(selected) => 
                                    handleChapterSelection(
                                      subject.id, 
                                      selected ? selected.map(option => option.value) : []
                                    )
                                  }
                                  placeholder="Choose chapters for practice..."
                                  className="react-select-container"
                                  classNamePrefix="react-select"
                                  styles={{
                                    control: (base) => ({
                                      ...base,
                                      borderColor: '#e9ecef',
                                      '&:hover': { borderColor: '#0d6efd' },
                                      boxShadow: 'none'
                                    })
                                  }}
                                />
                                <small className="text-muted mt-2 d-block">
                                  Optional: Select chapters you'd like to strengthen further
                                </small>
                              </motion.div>
                            )}

                            {strength?.level === 'very-strong' && (
                              <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                className="text-center py-5"
                              >
                                <i className="fas fa-star text-success fa-2x mb-2"></i>
                                <p className="text-muted">You're confident in this subject!</p>
                                <small className="text-muted">No additional chapters needed</small>
                              </motion.div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="d-flex justify-content-between">
              <button
                type="button"
                onClick={onBack}
                className="btn btn-outline-secondary btn-lg px-4"
              >
                <i className="fas fa-arrow-left me-2"></i>
                Back
              </button>
              
              <button
                type="button"
                onClick={handleSubmit}
                className="btn btn-primary btn-lg px-5"
                disabled={getRelevantSubjects().length !== subjectStrengths.length}
              >
                Continue
                <i className="fas fa-arrow-right ms-2"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default SubjectStrengthStep;